import { defineStore } from 'pinia'
import type { CorporateAccountUserProfile } from '@/models/api/corporate-account-user-profile'
import {
  basicUserProfileSchema,
  userProfileSchema,
  type BasicUserProfile,
  type UserProfile
} from '@/models/api/user-profile'
import { api } from '@/services'
import { useBookFlightPermissionStore } from '@/stores/use-book-flight-permission-store'
import { isLoading } from '@/utils/functions/is-loading'
import { useStorage } from '~library/composables'
import { Identity, LocalStorage } from '~library/enums'
import type { CorporateAccount } from '~library/models'
import { isApiSuccess, isType } from '~library/utils/functions'

type UserProfileData =
  | UserProfile
  | CorporateAccountUserProfile
  | BasicUserProfile
  | null

/**
 * 使用者帳號 Store
 *
 * 管理登入者的 userProfile、corporateAccount 等身份資料，
 * 並在 userProfile 變更時自動同步企業帳號資料。
 */
export const useUserStore = defineStore('user', () => {
  const _userProfile = ref<UserProfileData>(null)

  /** 企業帳號 - 登入者帳號 */
  const _corporateAccount = ref<string | null>(null)

  const _corporateAccountData = ref<CorporateAccount | null>(null)

  const { setStorageData, removeStorageData } = useStorage()

  /** 使用者的 bizId */
  const bizId = computed((): string | undefined => _userProfile.value?.bizId)

  /** 當前使用者資料 */
  const userProfile = computed((): UserProfileData => _userProfile.value)

  /** 企業帳號詳細資料 */
  const corporateAccountData = computed(
    (): CorporateAccount | null => _corporateAccountData.value
  )

  /** 企業帳號登入帳號 */
  const corporateAccount = computed((): string | null => {
    return _corporateAccount.value
  })

  /** FFP/SSO 會員卡號 */
  const memberCardNumber = computed((): string | undefined =>
    isType(userProfile.value, userProfileSchema) ||
    isType(userProfile.value, basicUserProfileSchema)
      ? userProfile.value.memberCardNumber
      : undefined
  )

  /** 是否為企業帳號身份 */
  const isCorporateAccount = computed(() => {
    return isCorporateAccountTypeGuide(userProfile.value)
  })

  /** CorporateAccountUserProfile 的 Type Guard */
  function isCorporateAccountTypeGuide(
    data: UserProfileData
  ): data is CorporateAccountUserProfile {
    return !!(
      data?.identity &&
      [Identity.CORPORATE_ACCOUNT, Identity.CORPORATE_SUB_ACCOUNT].includes(
        data.identity
      )
    )
  }

  /** 設定使用者資料並同步至 localStorage，同時更新企業帳號資料 */
  async function setUserProfile(data: UserProfileData) {
    _userProfile.value = data

    if (data) {
      setStorageData(LocalStorage.USER_PROFILE, data)
    } else {
      removeStorageData(LocalStorage.USER_PROFILE)
    }

    await setCorporateAccountByUserProfile()
  }

  function setCorporateAccountData(value: CorporateAccount | null) {
    _corporateAccountData.value = value
  }

  /** 設定企業帳號登入帳號並同步至 localStorage */
  function setCorporateAccount(value: string | null) {
    _corporateAccount.value = value

    if (value) {
      setStorageData(LocalStorage.CORPORATE_ACCOUNT, value)
    } else {
      removeStorageData(LocalStorage.CORPORATE_ACCOUNT)
    }
  }

  async function setCorporateAccountByUserProfile() {
    if (!isCorporateAccount.value) {
      setCorporateAccountData(null)
      return
    }
    const response = await isLoading(
      api.myAccount.getAdministratorInformation()
    )

    if (!isApiSuccess(response)) {
      setCorporateAccountData(null)
      return
    }

    // corporateAccount 為 null 代表尚未設定企業帳號，清空避免殘留舊資料
    if (!corporateAccount.value) {
      setCorporateAccountData(null)
      return
    }

    setCorporateAccountData(
      await getCorporateAccountData(corporateAccount.value)
    )
  }

  /** 重新取得企業帳號資料 */
  async function updateCorporateAccountData() {
    if (!corporateAccount.value) {
      return
    }

    setCorporateAccountData(
      await getCorporateAccountData(corporateAccount.value)
    )
  }

  async function getCorporateAccountData(
    account: string
  ): Promise<CorporateAccount | null> {
    const response = await isLoading(api.corporateAccount.getData(account))

    if (!isApiSuccess(response)) {
      return null
    }

    return response.data
  }

  /**
   * 清空使用者登入資訊
   */
  function clearUserProfile() {
    _userProfile.value = null
    _corporateAccount.value = null

    removeStorageData(LocalStorage.USER_PROFILE)
    removeStorageData(LocalStorage.CORPORATE_ACCOUNT)

    // 清除權限資料
    useBookFlightPermissionStore().clearPermissions()
  }

  return {
    /** 使用者的 bizId */
    bizId,
    /** 是否為企業帳號身份 */
    isCorporateAccount,
    /** CorporateAccountUserProfile 的 Type Guard */
    isCorporateAccountTypeGuide,
    /** 企業帳號登入帳號 */
    corporateAccount,
    /** FFP/SSO 會員卡號 */
    memberCardNumber,
    /** 當前使用者資料 */
    userProfile,
    /** 設定企業帳號登入帳號並同步至 localStorage */
    setCorporateAccount,
    /** 設定使用者資料並同步至 localStorage */
    setUserProfile,
    /** 清空使用者登入資訊 */
    clearUserProfile,
    /** 企業帳號詳細資料 */
    corporateAccountData,
    /** 重新取得企業帳號資料 */
    updateCorporateAccountData
  }
})
